import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { es } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toDisplay = (iso: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

export const displayToISO = (text: string) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (d.getMonth() !== Number(mm) - 1 || d.getDate() !== Number(dd)) return "";
  return `${yyyy}-${mm}-${dd}`;
};

const mask = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
};

export function DateInput({ id, value, onChange, testId, calendar = false }: { id: string; value: string; onChange: (iso: string) => void; testId: string; calendar?: boolean }) {
  const [text, setText] = useState(toDisplay(value));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (displayToISO(text) !== value) setText(toDisplay(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = (raw: string) => {
    const t = mask(raw);
    setText(t);
    onChange(displayToISO(t));
  };
  const invalid = text.length === 10 && !displayToISO(text);
  const pick = (d?: Date) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setText(toDisplay(iso));
    onChange(iso);
    setOpen(false);
  };
  const selected = value ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10))) : undefined;

  const input = (
    <Input
      id={id} type="text" inputMode="numeric" placeholder="dd/mm/aaaa" autoComplete="off" value={text}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handle(e.target.value)}
      className={`tabular h-11 ${calendar ? "pr-11" : ""} ${invalid ? "border-[#B23A1E]" : ""}`} data-testid={testId}
    />
  );
  if (!calendar) return input;
  return (
    <div className="relative">
      {input}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label="Elegir fecha en calendario" data-testid={`${testId}-calendar-button`}
            className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-[#1B2A4A] transition-colors hover:bg-[#F4F5F7]">
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto bg-white p-0" align="end" data-testid={`${testId}-calendar`}>
          <Calendar mode="single" locale={es} selected={selected} defaultMonth={selected} onSelect={pick} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}
